import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { House, Storefront, UsersThree, ShoppingCart, UserCircle } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import { useAuthModal } from "@/components/AuthModal";
import { useCart } from "@/lib/useCart";
import { useCurrency } from "@/lib/currency";
import { api } from "@/lib/api";
import useMobileNavScroll from "./mobile-nav/useMobileNavScroll";
import useLongPress from "./mobile-nav/useLongPress";
import CartPeek from "./mobile-nav/CartPeek";

const HIDE_PREFIXES = ["/admin", "/checkout", "/professional/dashboard", "/auth/"];
const CROSS_SELL_BELOW_INR = 1500;
const PEEK_DELAY_MS = 380;

/** Subtle haptic feedback on tap (mobile only). */
const tapFeedback = () => {
  try {
    if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
      navigator.vibrate(8);
    }
  } catch (e) {
    // navigator.vibrate is not available in every environment, debug-log so it's not silently swallowed.
    console.debug("[MobileBottomNav] vibrate unsupported:", e?.message || e);
  }
};

/** Compute the dashboard route for the currently signed-in user (or null when guest). */
function resolveAccountTarget(user) {
  if (!user) return null;
  if (user.role === "admin" || user.role === "founder") return "/admin";
  if (user.role === "professional") return "/professional/dashboard";
  return "/dashboard";
}

export default function MobileBottomNav() {
  const location = useLocation();
  const { user } = useAuth();
  const { open: openAuth } = useAuthModal();
  const { cart, count: cartCount, total: cartTotal, add: addCart } = useCart();
  const { format } = useCurrency();

  const { hidden, setHidden } = useMobileNavScroll();
  const [peekOpen, setPeekOpen] = useState(false);
  const [products, setProducts] = useState([]);

  // Lazy-load products once for cross-sell suggestions
  useEffect(() => {
    let alive = true;
    api.get("/products")
      .then((r) => { if (alive) setProducts(Array.isArray(r.data) ? r.data : []); })
      .catch((e) => console.debug("[MobileBottomNav] products fetch failed:", e?.message || e));
    return () => { alive = false; };
  }, []);

  // Always show nav + close peek on route change
  useEffect(() => {
    setHidden(false);
    setPeekOpen(false);
  }, [location.pathname, setHidden]);

  // Close peek when tapping outside
  useEffect(() => {
    if (!peekOpen) return;
    const onTap = (e) => {
      if (!e.target.closest(".md-bnav-peek") && !e.target.closest("[data-testid='bnav-cart']")) {
        setPeekOpen(false);
      }
    };
    document.addEventListener("touchstart", onTap, { passive: true });
    document.addEventListener("mousedown", onTap);
    return () => {
      document.removeEventListener("touchstart", onTap);
      document.removeEventListener("mousedown", onTap);
    };
  }, [peekOpen]);

  const { onPressStart, onPressEnd, guardClick } = useLongPress({
    enabled: cartCount > 0,
    delay: PEEK_DELAY_MS,
    onLongPress: () => { tapFeedback(); setPeekOpen(true); },
  });

  const onCartClick = useCallback((e) => {
    if (guardClick(e)) return;
    tapFeedback();
  }, [guardClick]);

  // Route gating, must run AFTER all hooks
  if (HIDE_PREFIXES.some((p) => location.pathname.startsWith(p))) return null;

  const isActive = (path, exact = false) => (
    exact ? location.pathname === path : (location.pathname === path || location.pathname.startsWith(path + "/"))
  );

  const accountTarget = resolveAccountTarget(user);

  const onAccountClick = (e) => {
    tapFeedback();
    if (!user) {
      e.preventDefault();
      openAuth("login");
    }
  };

  // Cross-sell: 2 products NOT already in cart, only if subtotal is below the threshold
  const inCart = new Set(cart.map((c) => c.id));
  const crossSells = (cartTotal > 0 && cartTotal < CROSS_SELL_BELOW_INR)
    ? products.filter((p) => !inCart.has(p.id)).slice(0, 2)
    : [];

  const addCrossSell = (p) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    tapFeedback();
    addCart(p);
  };

  return (
    <>
      {peekOpen && (
        <CartPeek
          cart={cart}
          cartCount={cartCount}
          cartTotal={cartTotal}
          crossSells={crossSells}
          format={format}
          onClose={() => setPeekOpen(false)}
          onAddCrossSell={addCrossSell}
          onTap={tapFeedback}
        />
      )}

      <nav
        className={`md-bottom-nav ${hidden ? "md-bottom-nav-hidden" : ""}`}
        role="navigation"
        aria-label="Primary mobile navigation"
        data-testid="mobile-bottom-nav"
      >
        <Link to="/" onClick={tapFeedback} className={`md-bnav-item ${isActive("/", true) ? "active" : ""}`} data-testid="bnav-home">
          <House size={22} weight={isActive("/", true) ? "fill" : "duotone"} />
          <span>Home</span>
        </Link>
        <Link to="/shop" onClick={tapFeedback} className={`md-bnav-item ${isActive("/shop") ? "active" : ""}`} data-testid="bnav-shop">
          <Storefront size={22} weight={isActive("/shop") ? "fill" : "duotone"} />
          <span>Shop</span>
        </Link>
        <Link to="/therapists" onClick={tapFeedback} className={`md-bnav-item ${isActive("/therapists") ? "active" : ""}`} data-testid="bnav-therapists">
          <UsersThree size={22} weight={isActive("/therapists") ? "fill" : "duotone"} />
          <span>Therapy</span>
        </Link>
        <Link
          to="/cart"
          onClick={onCartClick}
          onTouchStart={onPressStart}
          onTouchEnd={onPressEnd}
          onTouchCancel={onPressEnd}
          onMouseDown={onPressStart}
          onMouseUp={onPressEnd}
          onMouseLeave={onPressEnd}
          className={`md-bnav-item ${isActive("/cart") ? "active" : ""} ${peekOpen ? "peek-open" : ""}`}
          data-testid="bnav-cart"
          aria-label={cartCount > 0 ? `Cart, ${cartCount} items. Long-press to peek.` : "Cart"}
        >
          <span className="md-bnav-icon-wrap">
            <ShoppingCart size={22} weight={isActive("/cart") ? "fill" : "duotone"} />
            {cartCount > 0 && <span className="md-bnav-badge" data-testid="bnav-cart-badge">{cartCount}</span>}
          </span>
          <span>Cart</span>
        </Link>
        <Link
          to={accountTarget || "#"}
          onClick={onAccountClick}
          className={`md-bnav-item ${accountTarget && isActive(accountTarget) ? "active" : ""}`}
          data-testid="bnav-account"
        >
          <UserCircle size={22} weight={accountTarget && isActive(accountTarget) ? "fill" : "duotone"} />
          <span>{user ? "Me" : "Login"}</span>
        </Link>
      </nav>
    </>
  );
}
